import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import type { Grade } from '../../../types';
import { getGradeColorFromPercentage } from '../../../utils/gradeUtils';
import { pdfColors } from '../../../utils/pdf';

/** How a percentage is drawn in the report PDFs — always in the colour of the school's grade it falls in. */

/** The report PDFs' clear red and green — for a failed or absent paper, Fail and Pass, a fall and a rise. */
export const MARK_RED = '#dc2626';
export const MARK_GREEN = '#16a34a';

const styles = StyleSheet.create({
  pill: { alignSelf: 'center', borderRadius: 3, paddingVertical: 1.8, paddingHorizontal: 5 },
  pillText: { color: '#ffffff', fontSize: 7, fontWeight: 700 },
  bar: { flexDirection: 'row', alignItems: 'center' },
  track: { width: 30, height: 3.5, borderRadius: 2, backgroundColor: pdfColors.divider, marginRight: 5 },
  fill: { height: 3.5, borderRadius: 2 },
  barText: { fontSize: 7.5, fontWeight: 600, color: pdfColors.textPrimary },
  faint: { color: pdfColors.textFaint },
});

export const gradeColorOf = (grades: Grade[]) => (pct: number) => getGradeColorFromPercentage(pct, grades);

/** "78.0%" on its grade's colour; a quiet dash when there is no score. */
export const PctPill: React.FC<{ pct: number | null; grades: Grade[] }> = ({ pct, grades }) => (pct == null
  ? <Text style={styles.faint}>—</Text>
  : (
    <View style={[styles.pill, { backgroundColor: getGradeColorFromPercentage(pct, grades) }]}>
      <Text style={styles.pillText}>{pct.toFixed(1)}%</Text>
    </View>
  ));

/** A short bar filled to the score, in its grade's colour, then the figure. */
export const ScoreBar: React.FC<{ pct: number; grades: Grade[] }> = ({ pct, grades }) => (
  <View style={styles.bar}>
    <View style={styles.track}>
      <View style={[styles.fill, { width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: getGradeColorFromPercentage(pct, grades) }]} />
    </View>
    <Text style={styles.barText}>{pct.toFixed(1)}%</Text>
  </View>
);
